import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue'
import { ACTIVE, OFFLINE, STANDBY, UnitNumber } from '@/models/unit_number'
import { formatDateTime } from '@/utils/date-formatting.js'
import moment from 'moment'
import { useStore } from 'vuex-composition-helpers'
import UnitOfflineDialog from '@/components/common/UnitOfflineDialog.vue'
import FormLayout from '@/components/common/FormLayout.vue'
import {
  useResourceLocationQuery,
  useResourceQuery,
  useUpdateResourceStatusMutation,
} from '@/api/unit_numbers'
import User from '@/models/user'
import VsBottomSheet from '@/components/vision/VsBottomSheet.vue'
import ResourceForm from '@/components/units/ResourceForm.vue'
import ResourceJobSection from '@/components/field-view-next/ResourceJobSection.vue'
import { useMap } from '@/components/field-view-next/mapbox/context'
import { useFieldView } from '@/components/field-view-next/FieldViewState'
import Division from '@/models/division'
import Company from '@/models/company'
import { BusinessUnit } from '@/models/business-unit'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsText from '@/components/vision/VsText.vue'
import VsButton from '@/components/vision/VsButton.vue'

type UnitStatusIcon = {
  name: string
  helpText: string
}

export default /*#__PURE__*/_defineComponent({
  __name: 'FieldViewReadonlyResource',
  props: {
    id: null
  },
  emits: ['dismiss'],
  setup(__props: any, { emit }) {

const props = __props as {
  id: string | number
}




const store = useStore()
const { map } = useMap()
const { setSelectedResource } = useFieldView()

const showEditResourceForm = ref(false)
const showOfflineDialog = ref(false)

const getUserBySid = computed<(sid: string) => User>(
  () => store.getters['users/getBySid']
)
const idRef = computed(() => parseInt(props.id.toString(), 10))
const { data: resource, isLoading, refetch } = useResourceQuery(idRef)
const { data: latestLocation } = useResourceLocationQuery(idRef)
const { isLoading: isSaving, mutateAsync: updateResourceStatus } =
  useUpdateResourceStatusMutation()

const canPartiallyModifyResource = computed<boolean>(
  () => store.getters['permissions/canPartiallyModifyResource']
)
const getBusinessUnitById = computed<(id?: string) => BusinessUnit>(
  () => store.getters['businessUnits/getBusinessUnitById']
)
const resourceOwner = computed(() =>
  getBusinessUnitById.value(resource.value?.ownerId)
)
const currentUserOrgId = computed<string>(
  () => store.getters['permissions/getOrganizationId']
)
const canEditResource = computed(
  () =>
    resourceOwner.value?.id === currentUserOrgId.value ||
    resourceOwner.value?.ownerId === currentUserOrgId.value
)

const getDivisionById = computed<(id: number | null) => Division>(
  () => store.getters['divisions/getById']
)

const getCompanyByOrganizationId = computed<(id: string | null) => Company>(
  () => store.getters['companies/getByOrganizationId']
)

const division = computed(() =>
  getDivisionById.value(resource.value.divisionId)
)
const company = computed(() =>
  getCompanyByOrganizationId.value(resource.value.rootOrganizationId)
)

const overlineText = computed(() => {
  if (!resource.value) {
    return ''
  }

  const companyName = company.value?.name || ''
  const divisionName = division.value?.name || ''

  if (companyName && divisionName) {
    return `${divisionName}: ${companyName}`
  }

  return divisionName || companyName
})

const unitStatusIcon = computed<UnitStatusIcon>(() => {
  switch (resource.value?.status) {
    case OFFLINE:
      return {
        name: 'mdi-close-circle',
        helpText: 'Offline',
      }
    case STANDBY:
      return {
        name: 'pause_circle_filled',
        helpText: 'Standby',
      }
    case ACTIVE:
      return {
        name: 'play_circle_filled',
        helpText: 'Active',
      }
    default:
      return {
        name: 'help_outline',
        helpText: 'Loading',
      }
  }
})

const latestUpdatedInfo = computed<string | null>(() => {
  if (latestLocation.value && latestLocation.value.userSid) {
    const timestamp = moment.utc(latestLocation.value.timestamp).local()
    const user = getUserBySid.value(latestLocation.value.userSid) || {}
    return `${user.name} - ${timestamp.fromNow()}`
  }
  return null
})

const hasOperatorData = computed<boolean>(() => {
  return resource.value?.operator != null
})

const operatorName = computed(() => {
  const operator = resource.value?.operator
  if (operator && operator.userSid) {
    const user = getUserBySid.value(operator.userSid)
    if (user) {
      return user.name
    }
  }
  return 'No field workers on duty'
})

const onDutyColor = computed<string>(() => {
  if (resource.value?.operator?.onDuty) {
    return 'teal accent-2'
  }
  return 'grey lighten-1'
})

const onDutySubtext = computed<string | null>(() => {
  if (!hasOperatorData.value) return null
  if (resource.value?.operator?.onDuty) {
    return `On duty since ${formatDateTime(
      moment.utc(resource.value?.operator.occurredAt).local()
    )}`
  } else {
    return `Last on duty ${formatDateTime(
      moment.utc(resource.value?.operator?.occurredAt).local()
    )}`
  }
})

const statusChangeLabel = computed<string>(() => {
  return resource.value?.status === OFFLINE
    ? 'Mark as Online'
    : 'Mark as Offline'
})

const description = computed<string>(() => {
  if (!resource.value) {
    return ''
  }
  return [resource.value?.description, resource.value?.statusDescription]
    .filter(Boolean)
    .join('\n')
})

const handleStatusChange = async () => {
  if (resource.value?.status !== OFFLINE) {
    openOfflineDialog()
  } else {
    try {
      await updateResourceStatus({ id: resource.value?.id, status: STANDBY })
      await refetch()
    } catch (e) {}
  }
}

const setUnitStatusToOffline = async () => {
  try {
    await updateResourceStatus({ id: resource.value?.id, status: OFFLINE })
    await refetch()
  } catch (e) {}
  closeOfflineDialog()
}

const closeOfflineDialog = () => {
  showOfflineDialog.value = false
}

const openOfflineDialog = () => {
  showOfflineDialog.value = true
}
const handleDismiss = () => {
  emit('dismiss')
}

const handleEditingSheetDismiss = () => {
  showEditResourceForm.value = false
}

const handleEditingSheetSave = () => {
  showEditResourceForm.value = false
}

function flyToResource(resource: UnitNumber) {
  const coordinates = { lon: resource.longitude!, lat: resource.latitude! }
  map.value.flyTo({
    center: coordinates,
    zoom: 16,
    animate: map.value.getBounds().contains(coordinates),
  })
}

watch(
  resource,
  async (nextResource) => {
    if (nextResource) {
      setSelectedResource(nextResource)
    }

    if (nextResource) {
      flyToResource(nextResource)
    }
  },
  {
    immediate: true,
  }
)

return { __sfc: true,props, emit, store, map, setSelectedResource, showEditResourceForm, showOfflineDialog, getUserBySid, idRef, resource, isLoading, refetch, latestLocation, isSaving, updateResourceStatus, canPartiallyModifyResource, getBusinessUnitById, resourceOwner, currentUserOrgId, canEditResource, getDivisionById, getCompanyByOrganizationId, division, company, overlineText, unitStatusIcon, latestUpdatedInfo, hasOperatorData, operatorName, onDutyColor, onDutySubtext, statusChangeLabel, description, handleStatusChange, setUnitStatusToOffline, closeOfflineDialog, openOfflineDialog, handleDismiss, handleEditingSheetDismiss, handleEditingSheetSave, flyToResource, UnitOfflineDialog, FormLayout, VsBottomSheet, ResourceForm, ResourceJobSection, VsWrapper, VsText, VsButton }
}

})